import {ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners} from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideNativeDateAdapter} from '@angular/material/core';
import {provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {apiCredentialsInterceptor} from './interceptors/api-credentials-interceptor';
import {MERMAID_OPTIONS, provideMarkdown} from 'ngx-markdown';
import {I18nService} from './services/i18n-service';
import {ThemeService} from './services/theme-service';
import {NotificationService, AppNotificationStatus, AppNotificationType} from '@frednh/ai-agentic-core';
import {CHAT_LIBRARY_CONFIG_TOKEN, I18N_SERVICE_TOKEN, NOTIFICATION_SERVICE_TOKEN} from '@frednh/ai-agentic-core';
import {environment} from '../environments/environment';
import {UserManagementService} from './services/user-management-service';
import {CompanyService} from './services/company-service';
import {CrmPrompt} from './models/api/CrmPrompt';

registerLocaleData(localeFr, 'fr-FR');

function chatConfigFactory() {
  const userManagementService = inject(UserManagementService);
  const companyService = inject(CompanyService);


  return {
    apiUrl: environment.apiUrl,
    getContextualPrompt: ()  => {
      console.log("chatConfigFactory");
      const prompt : CrmPrompt = {
        prompt : "",
        llmServerName : "",
        conversationId : "",
        companyId : companyService.selectedCompanyId(),
        groupId : userManagementService.selectedGroupId()
      }
      return prompt;
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideMarkdown({
      mermaidOptions: {
        provide: MERMAID_OPTIONS,
        useValue: {
          startOnLoad: false,
          securityLevel: 'antiscript',
          theme: 'base',
          fontFamily: 'inherit',
          themeVariables: {
            primaryColor: 'var(--neutral900)',
            primaryTextColor: 'var(--neutral100)',
            primaryBorderColor: 'var(--brand-primary)',
            secondaryColor: 'var(--neutral1000)',
            secondaryTextColor: 'var(--neutral100)',
            secondaryBorderColor: 'var(--neutral700)',
            tertiaryColor: 'var(--neutral1000)',
            tertiaryTextColor: 'var(--neutral100)',
            tertiaryBorderColor: 'var(--neutral700)',
            lineColor: 'var(--neutral300)',
            mainBkg: 'var(--neutral900)',
            textColor: 'var(--neutral100)',
            nodeBorder: 'var(--brand-primary)',
            clusterBkg: 'var(--neutral1000)',
            clusterBorder: 'var(--neutral700)',
            edgeLabelBackground: 'var(--neutral1000)'
          }
        }
      }
    }),
    provideAnimations(),
    provideNativeDateAdapter(),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withFetch(), withInterceptors([apiCredentialsInterceptor])),
    provideAppInitializer(() => inject(I18nService).initialize()),
    provideAppInitializer(() => inject(ThemeService).initialize()),
    {
      provide: CHAT_LIBRARY_CONFIG_TOKEN,
      useFactory: chatConfigFactory
    },
    {
      provide: I18N_SERVICE_TOKEN,
      useFactory: () => {
        const i18nService = inject(I18nService);
        return {
          t: (key: string, params?: Record<string, string | number>) => i18nService.t(key, params),
          localeTag: () => i18nService.localeTag()
        };
      }
    },
    {
      provide: NOTIFICATION_SERVICE_TOKEN,
      useFactory: () => {
        const notificationService = inject(NotificationService);
        return {
          addNotification: (notification: {type: 'info' | 'success' | 'error', message: string}) => {
            notificationService.addNotification({
              type: AppNotificationType.CLIENT_SIDE,
              content: notification.message,
              code: '',
              targetUserId: '',
              status: notification.type === 'error' ? AppNotificationStatus.ACTIVE : AppNotificationStatus.NOT_APPLICABLE
            });
          }
        };
      }
    }
  ]
};
